import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import MedlemhosRow from './components/MedlemhosRow';

import { EndringsMelding } from 'services/QAApi/types';
import { EnhetViewModel, FirmaViewModel } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import {
  ChangeRequestCommand,
  Enhetsgruppe,
} from 'services/KjederegisteretAdminApi/ChangeRequestApi/types';

import { IFormData } from 'utils/hooks/useForm';
import Style from '../../index.module.css';
import { KjedeType } from 'services/KjederegisteretAdminApi/KjedeApi/types';

interface Props {
  formData: IFormData<ChangeRequestCommand, EnhetViewModel>;
  changeRequest: EndringsMelding;
  firma?: FirmaViewModel;
  enhet?: EnhetViewModel;
  duplicateWarning?: { orgnrDupes: string; nameDupes: string };
}

export interface KjedeInnmeldingsInfo {
  kjedeNr: string;
  innmeldingsdato: Date;
  medlemsnummer: string;
  kjedeType: KjedeType;
}
export interface KjedeUtmeldingsInfo {
  kjedeNr: string;
  utmeldingsdato: Date;
  kjedeType: KjedeType;
}
export interface InnkjopInnmeldingsInfo {
  enhetsgrupperingNr: number;
  innmeldingsdato: Date;
  medlemsnummer: string;
}
export interface InnkjopUtmeldingsInfo {
  enhetsgrupperingNr: number;
  utmeldingsdato: Date;
}

const QaMedlemhosForm = (props: Props) => {
  const [minFraOgMedDato, setMinFraOgMedDato] = useState<Date | undefined>(
    props.enhet?.oppstartsdato,
  );

  useEffect(() => {
    const getMinFraOgMedDato = async () => {
      if (props.enhet?.firma.forrigeLopenr) {
        try {
          const forrigeEnhet = await EnhetApi.getByLopenummer(props.enhet.firma.forrigeLopenr);
          if (forrigeEnhet?.firma.nedlagtDato) {
            // Day after nedlagtDato
            const dayAfterNedlagt = new Date(forrigeEnhet.firma.nedlagtDato);
            dayAfterNedlagt.setDate(dayAfterNedlagt.getDate() + 1);

            // 30 days before oppstartsdato
            const thirtyDaysBeforeOppstart = props.enhet?.oppstartsdato
              ? new Date(props.enhet.oppstartsdato)
              : null;
            if (thirtyDaysBeforeOppstart) {
              thirtyDaysBeforeOppstart.setDate(thirtyDaysBeforeOppstart.getDate() - 30);
            }

            // Use the later of the two dates (day after nedlagt, but not more than 30 days before oppstart)
            if (thirtyDaysBeforeOppstart && dayAfterNedlagt < thirtyDaysBeforeOppstart) {
              setMinFraOgMedDato(thirtyDaysBeforeOppstart);
            } else {
              setMinFraOgMedDato(dayAfterNedlagt);
            }
            return;
          }
        } catch (error) {
          console.error('Failed to fetch forrige enhet:', error);
        }
      }
      setMinFraOgMedDato(props.enhet?.oppstartsdato);
    };

    getMinFraOgMedDato();
  }, [props.enhet?.firma.forrigeLopenr, props.enhet?.oppstartsdato]);

  // Get active enhetsgrupperings if the unit has any
  const aktiveOgFremtidigeEnhetsgrupperinger =
    props.enhet?.grupper?.filter((o) => o.tilOgMedDato > new Date() || !o.tilOgMedDato) || [];

  // Map to Enhetsgruppe type for display
  const mappedAktiveGrupperinger: Enhetsgruppe[] = aktiveOgFremtidigeEnhetsgrupperinger.map(
    (g) => ({
      enhetsgrupperingsNr: g.enhetgrupperingnr?.toString() || null,
      enhetsgruppering: g.enhetsgrupperingnavn || '',
      medlemsnr: g.kundenr || null,
      innmeldingsdato: g.fraOgMedDato || null,
      toppnivaaEnhetsgrupperingnavn: g.toppnivaaEnhetsgrupperingnavn,
    }),
  );

  // Handle change for the enhetsgruppering selection
  const handleEnhetsgrupperingChange = (grupperingNr: string | null) => {
    if (!grupperingNr) {
      // If no grupperinger selected, update with empty array
      props.formData.handleChange('enhetsgrupperingsNr', null);
      return;
    }

    // Update the form data with the new grupper array (as number for consistency)
    props.formData.handleChange('enhetsgrupperingsNr', Number(grupperingNr));
  };

  const futureMarkedsKjede =
    props.enhet?.markedskjedeHistorikk.find((kjede) => kjede.isFuture) || null;

  const futureRegionalKjede =
    props.enhet?.regionalkjedeHistorikk.find((kjede) => kjede.isFuture) || null;

  const activeMarkedsKjede =
    props.enhet?.markedskjedeHistorikk.find((kjede) => kjede.isActive) || null;

  const activeRegionalKjede =
    props.enhet?.regionalkjedeHistorikk.find((kjede) => kjede.isActive) || null;

  // Get current active memberships for display
  const currentActiveMarkedskjede =
    props.enhet?.markedskjedeHistorikk.find((kjede) => kjede.isActive) || null;
  const currentActiveRegionalkjede =
    props.enhet?.regionalkjedeHistorikk.find((kjede) => kjede.isActive) || null;

  const handleKjedeInnmelding = (innmeldingsInfo: KjedeInnmeldingsInfo) => {
    if (innmeldingsInfo.kjedeType === KjedeType.Marked) {
      const copy = { ...props.formData.values };
      const parsed = Number(innmeldingsInfo.kjedeNr);
      copy.markedskjedeNr = Number.isNaN(parsed) || parsed === 0 ? null : parsed;
      copy.markedskjedeInnmeldingsdato = innmeldingsInfo.innmeldingsdato;
      copy.markedskjedeMedlemsnummer = innmeldingsInfo.medlemsnummer;
      props.formData.updateValues(copy);
    } else if (innmeldingsInfo.kjedeType === KjedeType.Region) {
      const copy = { ...props.formData.values };
      const parsed = Number(innmeldingsInfo.kjedeNr);
      copy.regionalkjedeNr = Number.isNaN(parsed) || parsed === 0 ? null : parsed;
      copy.regionalkjedeInnmeldingsdato = innmeldingsInfo.innmeldingsdato;
      copy.regionalkjedeMedlemsnummer = innmeldingsInfo.medlemsnummer;
      props.formData.updateValues(copy);
    }
  };

  const handleKjedeUtmelding = (utmeldingsInfo: KjedeUtmeldingsInfo) => {
    if (utmeldingsInfo.kjedeType === KjedeType.Marked) {
      // For markedskjede, set the values in the form data
      const copy = { ...props.formData.values };
      copy.markedskjedeUtmeldingsdato = utmeldingsInfo.utmeldingsdato;
      copy.markedskjedeNr = Number(utmeldingsInfo.kjedeNr);
      props.formData.updateValues(copy);
    } else if (utmeldingsInfo.kjedeType === KjedeType.Region) {
      // For regionalkjede, set the values in the form data
      const copy = { ...props.formData.values };
      copy.regionalkjedeUtmeldingsdato = utmeldingsInfo.utmeldingsdato;
      copy.regionalkjedeNr = Number(utmeldingsInfo.kjedeNr);
      props.formData.updateValues(copy);
    }
  };

  const handleInnkjopInnmelding = (innmeldingsInfo: InnkjopInnmeldingsInfo) => {
    const copy = { ...props.formData.values };
    copy.enhetsgrupperingsNr = innmeldingsInfo.enhetsgrupperingNr;
    copy.enhetsgrupperingsInnmeldingsdato = innmeldingsInfo.innmeldingsdato;
    copy.enhetsgrupperingsMedlemsnummer = innmeldingsInfo.medlemsnummer;
    props.formData.updateValues(copy);
  };

  const handleInnkjopUtmelding = (utmeldingsInfo: InnkjopUtmeldingsInfo) => {
    const copy = { ...props.formData.values };
    copy.enhetsgrupperingsUtmeldingsdato = utmeldingsInfo.utmeldingsdato;
    copy.enhetsgrupperingsNr = utmeldingsInfo.enhetsgrupperingNr;
    props.formData.updateValues(copy);
  };

  const getSelectedMarkedskjede = () => {
    // If user has manually selected, use form data, otherwise use current active
    return props.formData.values.markedskjedeNr
      ? {
          kjedeNr: Number(props.formData.values.markedskjedeNr),
          innmeldingsdato: props.formData.values.markedskjedeInnmeldingsdato,
          utmeldingsdato: props.formData.values.markedskjedeUtmeldingsdato,
          medlemsnummer: props.formData.values.markedskjedeMedlemsnummer,
        }
      : currentActiveMarkedskjede
        ? {
            kjedeNr: currentActiveMarkedskjede.kjedeId,
            innmeldingsdato: currentActiveMarkedskjede.fraOgMedDato,
            utmeldingsdato: currentActiveMarkedskjede.tilOgMedDato,
            medlemsnummer: currentActiveMarkedskjede.kundenrIKjede,
          }
        : undefined;
  };

  return (
    <div className={Style.medlemhosFormContainer}>
      <Form>
        <MedlemhosRow
          onKjedeInnmelding={(innmeldingsInfo) => handleKjedeInnmelding(innmeldingsInfo)}
          onKjedeUtmelding={(utmeldingsInfo) => handleKjedeUtmelding(utmeldingsInfo)}
          onInnkjopInnmelding={(innmeldingsInfo) => handleInnkjopInnmelding(innmeldingsInfo)}
          onInnkjopUtmelding={(utmeldingsInfo) => handleInnkjopUtmelding(utmeldingsInfo)}
          onChange={(field, value) => props.formData.handleChange(field, value)}
          changeRequest={{
            id: props.changeRequest.id,
            medlemHos: props.changeRequest.changes.medlemHos,
            medlemsNummer: props.changeRequest.changes.medlemsNummer,
            medlemFraOgMedDato: props.changeRequest.changes.medlemFraOgMedDato,
            medlemTilOgMedDato: props.changeRequest.changes.medlemTilOgMedDato,
            medlemHosEkstraInfo: props.changeRequest.membershipSuggestions,
          }}
          error={
            props.formData.touched.medlemHos && props.formData.errors.medlemHos
              ? props.formData.errors.medlemHos
              : undefined
          }
          // Enhetsgruppering/innkjøp props
          selectedEnhetsgruppering={{
            enhetsgrupperingNr: props.formData.values.enhetsgrupperingsNr,
            innmeldingsdato: props.formData.values.enhetsgrupperingsInnmeldingsdato,
            utmeldingsdato: props.formData.values.enhetsgrupperingsUtmeldingsdato,
            medlemsnummer: props.formData.values.enhetsgrupperingsMedlemsnummer,
          }}
          aktiveEnhetsgrupperinger={mappedAktiveGrupperinger}
          onEnhetsgrupperingChange={handleEnhetsgrupperingChange}
          enhetsgrupperingError={
            props.formData.touched.grupper && props.formData.errors.grupper
              ? props.formData.errors.grupper
              : undefined
          }
          // Regionalkjede props
          selectedRegionalkjede={
            // If user has manually selected, use form data, otherwise use current active
            props.formData.values.regionalkjedeNr
              ? {
                  kjedeNr: Number(props.formData.values.regionalkjedeNr),
                  innmeldingsdato: props.formData.values.regionalkjedeInnmeldingsdato,
                  utmeldingsdato: props.formData.values.regionalkjedeUtmeldingsdato,
                  medlemsnummer: props.formData.values.regionalkjedeMedlemsnummer,
                }
              : currentActiveRegionalkjede
                ? {
                    kjedeNr: currentActiveRegionalkjede.kjedeId,
                    innmeldingsdato: currentActiveRegionalkjede.fraOgMedDato,
                    utmeldingsdato: currentActiveRegionalkjede.tilOgMedDato,
                    medlemsnummer: currentActiveRegionalkjede.kundenrIKjede,
                  }
                : undefined
          }
          regionalkjedeError={
            props.formData.touched.regionalkjedeNr && props.formData.errors.regionalkjedeNr
              ? props.formData.errors.regionalkjedeNr
              : undefined
          }
          futureRegionalkjede={futureRegionalKjede}
          activeRegionalkjede={activeRegionalKjede}
          // Markedskjede props
          selectedMarkedskjede={getSelectedMarkedskjede()}
          markedskjedeError={
            props.formData.touched.markedskjedeNr && props.formData.errors.markedskjedeNr
              ? props.formData.errors.markedskjedeNr
              : undefined
          }
          futureMarkedsKjede={futureMarkedsKjede}
          activeMarkedskjede={activeMarkedsKjede}
          minFraOgMedDato={minFraOgMedDato}
        />
      </Form>
    </div>
  );
};

export default QaMedlemhosForm;
